import { Check, Hexagon } from "lucide-react";

/**
 * StylePresetChip — a "spell-card" style preset picker used in Studio's Skills
 * popover and the classic editor. Displays the style thumbnail, name, and accent
 * hexagon; glows in gold when active, cyan on hover.
 *
 * Props:
 *   preset:  { id, name, desc, thumb, accent, group }
 *   active:  boolean
 *   onClick: () => void
 *   size:    "sm" | "md" (default "md")
 *   testId
 */
export function StylePresetChip({ preset, active, onClick, size = "md", testId }) {
  const isSm = size === "sm";
  return (
    <button
      onClick={onClick}
      data-testid={testId || `style-chip-${preset.id}`}
      title={`${preset.name} — ${preset.desc}`}
      className={`group relative overflow-hidden rounded-2xl border transition-all duration-500 hover:-translate-y-0.5 ${
        isSm ? "aspect-[5/4]" : "aspect-[4/5]"
      } ${
        active
          ? "border-[#F2C94C] shadow-[0_0_28px_-4px_rgba(242,201,76,0.75)] ring-1 ring-[#F2C94C]/60"
          : "border-white/[0.08] hover:border-[#00E5FF]/70 hover:shadow-[0_0_28px_-8px_rgba(0,229,255,0.55)]"
      }`}
    >
      {/* Thumbnail */}
      {preset.thumb ? (
        <img
          src={preset.thumb}
          alt={preset.name}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
            active ? "scale-[1.04]" : "group-hover:scale-[1.06]"
          }`}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${preset.accent || "#F2C94C"}55, transparent 60%), #0A0A0A` }} />
      )}
      {/* Dim & vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/65 to-transparent" />
      {/* Rim aurora (hover) */}
      <span aria-hidden className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(80% 60% at 50% 100%, ${preset.accent || "#00E5FF"}44, transparent 70%)` }} />
      {/* Active checkmark */}
      {active && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#F2C94C] text-[#0A0A0A] flex items-center justify-center shadow-[0_0_18px_-2px_rgba(242,201,76,0.9)]">
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </div>
      )}
      {/* Name row */}
      <div className={`absolute inset-x-0 bottom-0 p-2.5 text-left ${isSm ? "space-y-0.5" : "space-y-1"}`}>
        <div className="flex items-center gap-1.5">
          <Hexagon className="w-2.5 h-2.5" style={{ color: preset.accent || "#F2C94C" }} fill={active ? preset.accent : "transparent"} />
          <span className={`font-display text-white leading-tight ${isSm ? "text-[11px]" : "text-xs"}`}>{preset.name}</span>
        </div>
        {!isSm && preset.desc && (
          <div className="text-[9px] text-white font-mono-hy leading-tight line-clamp-2">{preset.desc}</div>
        )}
      </div>
    </button>
  );
}

export default StylePresetChip;
